import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const TermsAndConditions = () => {    
    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`Terms and Conditions – ${process.env.REACT_APP_NAME} Platform Guidelines`}</title>
            <meta
                name="description"
                content="Review the terms and conditions governing the use of Hunar Pool, including user responsibilities, platform policies, and service guidelines."
            />

            {/* Page Header */}
            <div className="text-center mb-12">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Terms &amp; <span className="gradient-text">Conditions</span>
                </h1>
                <p className="text-gray-300 text-lg">Last Updated: January 15, 2026</p>
            </div>
            {/* Important Notice */}
            <div className="highlight-box mb-8">
                <div className="flex items-start">
                <i className="fas fa-exclamation-circle text-[#0096C7] text-xl mt-1 mr-3" />
                <div>
                    <p className="font-bold mb-2">IMPORTANT NOTICE</p>
                    <p className="text-gray-300">
                    Please read these Terms &amp; Conditions carefully before using Hunar
                    Pool. By accessing or using our platform, you agree to be bound by
                    these terms. If you disagree with any part of these terms, you may not
                    access the service.
                    </p>
                </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="section-bg rounded-2xl p-6 mb-8 border border-gray-800">
                <h2 className="text-xl font-bold mb-4">Quick Navigation</h2>
                <div className="flex flex-wrap gap-3">
                <a
                    href="#acceptance"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Acceptance
                </a>
                <a
                    href="#accounts"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Accounts
                </a>
                <a
                    href="#services"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Services
                </a>
                <a
                    href="#user-content"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    User Content
                </a>
                <a
                    href="#prohibited"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Prohibited Uses
                </a>
                <a
                    href="#termination"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Termination
                </a>
                <a
                    href="#disclaimer"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Disclaimer
                </a>
                <a
                    href="#limitation"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Limitation
                </a>
                </div>
            </div>

            {/* Terms Content */}
            <div className="section-bg rounded-2xl p-8 border border-gray-800 content-section">
                <div id="acceptance" className="mb-12">
                <h2>1. Acceptance of Terms</h2>
                <p>
                    By accessing and using the Hunar Pool website and services
                    (collectively, the "Service"), you accept and agree to be bound by the
                    terms and provision of this agreement. In addition, when using these
                    services, you shall be subject to any posted guidelines or rules
                    applicable to such services.
                </p>
                <p>
                    If you do not agree to these Terms, please do not use our Service. We
                    reserve the right to change these Terms from time to time without
                    notice. You acknowledge and agree that it is your responsibility to
                    review these Terms periodically to familiarize yourself with any
                    modifications.
                </p>
                </div>
                <div id="accounts" className="mb-12">
                <h2>2. User Accounts and Registration</h2>
                <h3>2.1 Account Creation</h3>
                <p>
                    To access certain features of the Service, you may be required to
                    register for an account. You agree to provide accurate, current, and
                    complete information during the registration process and to update such
                    information to keep it accurate, current, and complete.
                </p>
                <h3>2.2 Account Security</h3>
                <p>
                    You are responsible for safeguarding the password that you use to access
                    the Service and for any activities or actions under your password. We
                    encourage you to use "strong" passwords (passwords that use a
                    combination of upper and lower case letters, numbers, and symbols).
                </p>
                <h3>2.3 Account Responsibility</h3>
                <p>
                    You agree not to disclose your password to any third party. You must
                    notify us immediately upon becoming aware of any breach of security or
                    unauthorized use of your account.
                </p>
                </div>
                <div id="services" className="mb-12">
                <h2>3. Services Description</h2>
                <p>Hunar Pool provides an AI-powered job matching platform that:</p>
                <ul>
                    <li>Connects job seekers with employment opportunities</li>
                    <li>
                    Uses artificial intelligence to match candidates with suitable jobs
                    </li>
                    <li>Provides job search tools and career resources</li>
                    <li>Facilitates communication between job seekers and employers</li>
                </ul>
                <div className="highlight-box my-6">
                    <p>
                    <strong>Note:</strong> Hunar Pool is a matching platform. We do not
                    guarantee employment, and we are not responsible for hiring decisions
                    made by employers.
                    </p>
                </div>
                <h3>3.1 Free and Premium Services</h3>
                <p>
                    Basic job search and matching features are provided free of charge. We
                    may offer premium features for a fee, which will be clearly indicated on
                    the platform.
                </p>
                </div>
                <div id="user-content" className="mb-12">
                <h2>4. User Content</h2>
                <h3>4.1 Content Ownership</h3>
                <p>
                    You retain ownership of any intellectual property rights that you hold
                    in the content you submit, post, or display on or through the Service
                    ("User Content").
                </p>
                <h3>4.2 License Grant</h3>
                <p>
                    By submitting User Content, you grant Hunar Pool a worldwide,
                    non-exclusive, royalty-free license to use, reproduce, modify, adapt,
                    publish, translate, create derivative works from, distribute, and
                    display such content in connection with providing the Service.
                </p>
                <h3>4.3 Content Standards</h3>
                <p>You agree not to submit User Content that:</p>
                <ul>
                    <li>Is illegal, offensive, or inappropriate</li>
                    <li>
                    Infringes any patent, trademark, trade secret, copyright, or other
                    intellectual property rights
                    </li>
                    <li>Contains false or misleading information</li>
                    <li>
                    Contains viruses or any other computer code designed to interrupt,
                    destroy, or limit the functionality of any computer software or
                    hardware
                    </li>
                </ul>
                </div>
                <div id="prohibited" className="mb-12">
                <h2>5. Prohibited Uses</h2>
                <p>You agree not to use the Service:</p>
                <ul>
                    <li>For any unlawful purpose or to promote illegal activities</li>
                    <li>
                    To harass, abuse, insult, harm, defame, slander, disparage,
                    intimidate, or discriminate
                    </li>
                    <li>To submit false or misleading information</li>
                    <li>
                    To upload or transmit viruses or any other type of malicious code
                    </li>
                    <li>To collect or track the personal information of others</li>
                    <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
                    <li>For any obscene or immoral purpose</li>
                    <li>
                    To interfere with or circumvent the security features of the Service
                    </li>
                </ul>
                <h3>5.1 Specific Prohibitions for Job Seekers</h3>
                <ul>
                    <li>Applying for jobs for which you are not qualified</li>
                    <li>
                    Submitting false or misleading information in your profile or
                    applications
                    </li>
                    <li>
                    Creating multiple accounts to manipulate job matching algorithms
                    </li>
                </ul>
                </div>
                <div id="termination" className="mb-12">
                <h2>6. Termination</h2>
                <p>
                    We may terminate or suspend your account and bar access to the Service
                    immediately, without prior notice or liability, under our sole
                    discretion, for any reason whatsoever and without limitation, including
                    but not limited to a breach of the Terms.
                </p>
                <p>
                    If you wish to terminate your account, you may simply discontinue using
                    the Service or contact us to request account deletion.
                </p>
                <p>
                    All provisions of the Terms which by their nature should survive
                    termination shall survive termination, including, without limitation,
                    ownership provisions, warranty disclaimers, indemnity, and limitations
                    of liability.
                </p>
                </div>
                <div id="disclaimer" className="mb-12">
                <h2>7. Disclaimer of Warranties</h2>
                <p>
                    THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. HUNAR
                    POOL AND ITS AFFILIATES, PARTNERS, AND LICENSORS EXPRESSLY DISCLAIM ALL
                    WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT
                    LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
                <p>WE MAKE NO WARRANTY THAT:</p>
                <ul>
                    <li>THE SERVICE WILL MEET YOUR REQUIREMENTS</li>
                    <li>
                    THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE
                    </li>
                    <li>
                    THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE SERVICE WILL BE
                    ACCURATE OR RELIABLE
                    </li>
                    <li>
                    THE QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR OTHER MATERIAL
                    OBTAINED BY YOU THROUGH THE SERVICE WILL MEET YOUR EXPECTATIONS
                    </li>
                </ul>
                </div>
                <div id="limitation" className="mb-12">
                <h2>8. Limitation of Liability</h2>
                <p>
                    IN NO EVENT SHALL HUNAR POOL, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS,
                    AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                    WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER
                    INTANGIBLE LOSSES, RESULTING FROM:
                </p>
                <ul>
                    <li>
                    YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE
                    </li>
                    <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE</li>
                    <li>ANY CONTENT OBTAINED FROM THE SERVICE</li>
                    <li>
                    UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR
                    CONTENT
                    </li>
                </ul>
                <p>
                    THIS LIMITATION APPLIES WHETHER THE ALLEGED LIABILITY IS BASED ON
                    CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR ANY OTHER BASIS, EVEN
                    IF HUNAR POOL HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
                </p>
                </div>
                <div className="mb-12">
                <h2>9. Indemnification</h2>
                <p>
                    You agree to defend, indemnify, and hold harmless Hunar Pool and its
                    licensees and licensors, and their employees, contractors, agents,
                    officers, and directors, from and against any and all claims, damages,
                    obligations, losses, liabilities, costs or debt, and expenses (including
                    but not limited to attorney's fees), resulting from or arising out of:
                </p>
                <ul>
                    <li>Your use and access of the Service</li>
                    <li>A breach of these Terms</li>
                    <li>Content posted on the Service</li>
                    <li>Your violation of any law or the rights of a third party</li>
                </ul>
                </div>
                <div className="mb-12">
                <h2>10. Governing Law</h2>
                <p>
                    These Terms shall be governed and construed in accordance with the laws
                    of the State of California, United States, without regard to its
                    conflict of law provisions.
                </p>
                <p>
                    Our failure to enforce any right or provision of these Terms will not be
                    considered a waiver of those rights. If any provision of these Terms is
                    held to be invalid or unenforceable by a court, the remaining provisions
                    of these Terms will remain in effect.
                </p>
                </div>
                <div className="mb-12">
                <h2>11. Changes to Terms</h2>
                <p>
                    We reserve the right, at our sole discretion, to modify or replace these
                    Terms at any time. If a revision is material, we will try to provide at
                    least 30 days' notice prior to any new terms taking effect. What
                    constitutes a material change will be determined at our sole discretion.
                </p>
                <p>
                    By continuing to access or use our Service after those revisions become
                    effective, you agree to be bound by the revised terms. If you do not
                    agree to the new terms, please stop using the Service.
                </p>
                </div>
                <div>
                <h2>12. Contact Information</h2>
                <p>If you have any questions about these Terms, please contact us:</p>
                <ul>
                    <li>
                    <strong>Email:</strong> legal@hunarpool.com
                    </li>
                    <li>
                    <strong>Address:</strong> Hunar Pool, 123 Tech Street, San Francisco,
                    CA 94107
                    </li>
                    <li>
                    <strong>Phone:</strong> +1 (555) 123-4567
                    </li>
                </ul>
                </div>
            </div>
            {/* Acceptance Confirmation */}
            <div className="section-bg rounded-2xl p-8 mt-8 border border-gray-800">
                <div className="flex items-start">
                <i className="fas fa-check-circle text-[#06D6A0] text-2xl mt-1 mr-4" />
                <div>
                    <h3 className="text-xl font-bold mb-4">Acceptance Confirmation</h3>
                    <p className="text-gray-300 mb-4">
                    By using Hunar Pool, you acknowledge that you have read, understood,
                    and agree to be bound by these Terms &amp; Conditions.
                    </p>
                    <p className="text-gray-300">
                    For questions or concerns about these terms, please contact our legal
                    team at legal@hunarpool.com.
                    </p>
                </div>
                </div>
            </div>
        </div>
    );
};

export default TermsAndConditions;